import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * The cart drawer.
 *
 * A cart change starts the re-render immediately and `open()` waits on
 * whatever is in flight. The add-to-cart path opens the drawer straight after
 * changing the cart, so starting the fetch when the change lands rather than
 * when the drawer opens overlaps it with the animation instead of stacking a
 * round trip in front of an empty panel. Waiting on the same promise is what
 * keeps it to one fetch either way.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {Promise<void> | null} */
  #refreshing = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  async open() {
    // Never rejects: refresh swallows its own failure, so a section render
    // that fails still opens the drawer on the markup already there.
    await this.#refreshing;
    super.open();
  }

  refresh() {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return Promise.resolve();

    const refreshing = sectionRenderer
      .render(sectionId)
      .then(() => undefined)
      .catch((error) => console.warn('[cart-drawer]', error))
      .finally(() => {
        // Only clear the handle if it is still mine. A second refresh aborts
        // the first, whose promise then settles early; clearing unconditionally
        // would leave `open()` no longer waiting on the one still running.
        if (this.#refreshing === refreshing) this.#refreshing = null;
      });

    this.#refreshing = refreshing;
    return refreshing;
  }

  #onCartUpdate = () => {
    this.refresh();
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
